import { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { addSprint, updateSprint, updateTask } from '../store';
import { Task, Sprint, SprintAllocation } from '../types';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Plus, Calendar, ArrowLeft, AlertCircle, CheckCircle2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../providers/AppProvider';
import CreateSprintModal from '../components/CreateSprintModal';
import { useOrganizationData } from '../hooks/useOrganizationData';
import { parseDateLike } from '../utils/dateUtils';

const toTime = (value: unknown): number => parseDateLike(value)?.getTime() ?? 0;

const formatDate = (value: unknown): string => {
  const parsedDate = parseDateLike(value);
  return parsedDate ? parsedDate.toLocaleDateString() : 'Unknown date';
};

export default function SprintPlanningBoard() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { state, loading, reload } = useApp();
  const { projects, tasks, sprints } = useOrganizationData(state) as {
    projects: any[];
    tasks: Task[];
    sprints: Sprint[];
  };
  const [showCreateSprint, setShowCreateSprint] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [allocationHours, setAllocationHours] = useState<Record<string, string>>({});

  if (loading || !state) {
    return <div className="p-8 text-center text-muted-foreground">Loading sprint planning...</div>;
  }

  const project = projects.find(p => p.id === projectId);
  const projectTasks = tasks.filter(t => t.projectId === projectId);
  const projectSprints = sprints.filter(s => s.projectId === projectId).sort((a, b) => 
    toTime(a.startDate) - toTime(b.startDate)
  );

  // Get unallocated or partially allocated tasks
  const getTaskAllocationStatus = (task: Task) => {
    const totalAllocated = (task.sprintAllocations || []).reduce((sum, a) => sum + a.allocatedHours, 0);
    const remaining = (task.estimatedHours || 0) - totalAllocated;
    return { totalAllocated, remaining };
  };

  const tasksNeedingAllocation = projectTasks.filter(t => {
    const { remaining } = getTaskAllocationStatus(t);
    return remaining > 0 && t.estimatedHours && t.estimatedHours > 0;
  });

  const handleQuickAllocate = async (task: Task, sprint: Sprint) => {
    const key = `${task.id}-${sprint.id}`;
    const hours = parseFloat(allocationHours[key] || '0');

    if (hours <= 0) {
      toast.error('Please enter valid hours');
      return;
    }

    const { remaining } = getTaskAllocationStatus(task);
    if (hours > remaining) {
      toast.error(`Only ${remaining.toFixed(1)}h remaining`);
      return;
    }

    // Check if already allocated to this sprint
    const existingAllocations = task.sprintAllocations || [];
    if (existingAllocations.some(a => a.sprintId === sprint.id)) {
      toast.error('Task already allocated to this sprint');
      return;
    }

    const newAllocation: SprintAllocation = {
      sprintId: sprint.id,
      sprintName: sprint.name,
      allocatedHours: hours,
      actualHours: 0,
      status: 'pending',
    };

    const updatedAllocations = [...existingAllocations, newAllocation];
    await updateTask(task.id, { sprintAllocations: updatedAllocations });

    // Add task to sprint's taskIds if not already there
    const sprintTaskIds = sprint.taskIds || [];
    if (!sprintTaskIds.includes(task.id)) {
      const updatedTaskIds = [...sprintTaskIds, task.id];
      await updateSprint(sprint.id, { taskIds: updatedTaskIds });
    }

    if (state?.currentUser?.email) {
      await reload(state.currentUser.email);
    }
    setAllocationHours({ ...allocationHours, [key]: '' });
    toast.success(`Allocated ${hours}h to ${sprint.name}`);
  };

  const getSprintStats = (sprint: Sprint) => {
    const sprintTasks = projectTasks.filter(t => 
      (t.sprintAllocations || []).some(a => a.sprintId === sprint.id)
    );
    
    const totalAllocated = sprintTasks.reduce((sum, t) => {
      const allocation = t.sprintAllocations?.find(a => a.sprintId === sprint.id);
      return sum + (allocation?.allocatedHours || 0);
    }, 0);

    const totalActual = sprintTasks.reduce((sum, t) => {
      const allocation = t.sprintAllocations?.find(a => a.sprintId === sprint.id);
      return sum + (allocation?.actualHours || 0);
    }, 0);

    const taskCount = sprintTasks.length;

    return { totalAllocated, totalActual, taskCount };
  };

  if (!project) {
    return (
      <div className="container mx-auto px-4 py-6 text-center">
        <p className="text-muted-foreground">Project not found</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/app/projects/${projectId}/sprints`)}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Sprint Management
        </Button>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="mb-2 text-3xl font-bold text-foreground">Sprint Planning Board</h1>
            <p className="text-muted-foreground">{project.name}</p>
          </div>
          <Button onClick={() => setShowCreateSprint(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Sprint
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Sprints</p>
                <p className="text-2xl font-bold">{projectSprints.length}</p>
              </div>
              <Calendar className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Tasks Needing Allocation</p>
                <p className="text-2xl font-bold text-foreground">{tasksNeedingAllocation.length}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Fully Allocated Tasks</p>
                <p className="text-2xl font-bold text-foreground">
                  {projectTasks.filter(t => {
                    const { remaining } = getTaskAllocationStatus(t);
                    return remaining === 0 && t.estimatedHours && t.estimatedHours > 0;
                  }).length}
                </p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Unallocated Tasks Column */}
        <div className="lg:col-span-1">
          <Card className="sticky top-4">
            <CardHeader className="border-border bg-muted/40">
              <CardTitle className="flex items-center gap-2 text-foreground">
                <AlertCircle className="w-5 h-5" />
                Tasks Needing Allocation
                <Badge variant="secondary" className="ml-auto">
                  {tasksNeedingAllocation.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3 max-h-[600px] overflow-y-auto">
              {tasksNeedingAllocation.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  <CheckCircle2 className="mx-auto mb-2 h-12 w-12 text-primary" />
                  <p>All tasks are allocated!</p>
                </div>
              ) : (
                tasksNeedingAllocation.map((task) => {
                  const { totalAllocated, remaining } = getTaskAllocationStatus(task);
                  const estimatedHours = task.estimatedHours || 0;
                  const percentAllocated = estimatedHours > 0 ? (totalAllocated / estimatedHours) * 100 : 0;
                  const boundedPercentAllocated = Math.min(percentAllocated, 100);

                  return (
                    <Card
                      key={task.id}
                      className="cursor-pointer border-border transition-shadow hover:bg-muted/30 hover:shadow-md"
                      onClick={() => setSelectedTask(task)}
                    >
                      <CardContent className="p-3">
                        <div className="space-y-2">
                          <div>
                            <Badge variant="outline" className="text-xs mb-1">
                              {task.id}
                            </Badge>
                            <h4 className="font-semibold text-sm line-clamp-2">
                              {task.title}
                            </h4>
                          </div>

                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="rounded bg-muted/60 p-1.5">
                              <div className="font-semibold text-foreground">Estimate</div>
                              <div className="font-bold">{estimatedHours}h</div>
                            </div>
                            <div className="rounded bg-muted/60 p-1.5">
                              <div className="font-semibold text-foreground">Remaining</div>
                              <div className="font-bold">{remaining.toFixed(1)}h</div>
                            </div>
                          </div>

                          {totalAllocated > 0 && (
                            <div>
                              <div className="mb-1 flex justify-between text-xs text-muted-foreground">
                                <span>Allocated</span>
                                <span>{percentAllocated.toFixed(0)}%</span>
                              </div>
                              <Progress value={boundedPercentAllocated} className="h-1.5" />
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sprints Column */}
        <div className="lg:col-span-2 space-y-4">
          {projectSprints.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Calendar className="mx-auto mb-4 h-16 w-16 text-muted-foreground" />
                <p className="mb-4 text-muted-foreground">No sprints created yet</p>
                <Button onClick={() => setShowCreateSprint(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Sprint
                </Button>
              </CardContent>
            </Card>
          ) : (
            projectSprints.map((sprint) => {
              const stats = getSprintStats(sprint);
              const sprintTasks = projectTasks.filter(t =>
                (t.sprintAllocations || []).some(a => a.sprintId === sprint.id)
              );

              return (
                <Card
                  key={sprint.id}
                  className="border-2 border-border transition-colors hover:border-ring"
                >
                  <CardHeader className="border-border bg-muted/40">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <CardTitle className="text-lg">{sprint.name}</CardTitle>
                          <Badge
                            variant={
                              sprint.status === 'Active'
                                ? 'default'
                                : sprint.status === 'Completed'
                                ? 'secondary'
                                : 'outline'
                            }
                          >
                            {sprint.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            <span>
                              {formatDate(sprint.startDate)} - {formatDate(sprint.endDate)}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            <span>{stats.taskCount} tasks</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Sprint Stats */}
                    <div className="grid grid-cols-3 gap-3 mt-4">
                      <div className="rounded bg-card p-2 text-center">
                        <div className="text-xs text-muted-foreground">Allocated</div>
                        <div className="text-lg font-bold text-foreground">
                          {stats.totalAllocated.toFixed(1)}h
                        </div>
                      </div>
                      <div className="rounded bg-card p-2 text-center">
                        <div className="text-xs text-muted-foreground">Actual</div>
                        <div className="text-lg font-bold text-foreground">
                          {stats.totalActual.toFixed(1)}h
                        </div>
                      </div>
                      <div className="rounded bg-card p-2 text-center">
                        <div className="text-xs text-muted-foreground">Variance</div>
                        <div
                          className={`text-lg font-bold ${
                            stats.totalActual > stats.totalAllocated ? 'text-destructive' : 'text-muted-foreground'
                          }`}
                        >
                          {(stats.totalActual - stats.totalAllocated).toFixed(1)}h
                        </div>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="p-4">
                    {/* Allocated Tasks */}
                    {sprintTasks.length > 0 && (
                      <div className="mb-4 space-y-2">
                        <h4 className="mb-2 text-sm font-semibold text-foreground">Allocated Tasks</h4>
                        {sprintTasks.map((task) => {
                          const allocation = task.sprintAllocations?.find(a => a.sprintId === sprint.id);
                          if (!allocation) return null;

                          return (
                            <Card
                              key={task.id}
                              className="cursor-pointer border-border hover:bg-muted/40"
                              onClick={() => navigate(`/task/${task.id}`)}
                            >
                              <CardContent className="p-3">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex-1 min-w-0">
                                    <Badge variant="outline" className="text-xs mb-1">
                                      {task.id}
                                    </Badge>
                                    <p className="text-sm font-medium truncate">{task.title}</p>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    <div className="text-right">
                                      <div className="text-xs text-muted-foreground">Allocated</div>
                                      <div className="font-bold text-foreground">
                                        {allocation.allocatedHours}h
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <div className="text-xs text-muted-foreground">Actual</div>
                                      <div className="font-bold text-foreground">
                                        {(allocation.actualHours || 0).toFixed(1)}h
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    )}

                    {/* Quick Allocate Section */}
                    {selectedTask && (
                      <div className="mt-4 border-t border-border pt-4">
                        <div className="mb-3 rounded-lg border border-primary/30 bg-primary/10 p-3">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
                            <div className="flex-1">
                              <p className="mb-1 text-sm font-semibold text-foreground">
                                Selected: {selectedTask.title}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {getTaskAllocationStatus(selectedTask).remaining.toFixed(1)}h remaining to allocate
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedTask(null)}
                            >
                              ×
                            </Button>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <Input
                            type="number"
                            min="0"
                            step="0.5"
                            placeholder="Hours"
                            value={allocationHours[`${selectedTask.id}-${sprint.id}`] || ''}
                            onChange={(e) =>
                              setAllocationHours({
                                ...allocationHours,
                                [`${selectedTask.id}-${sprint.id}`]: e.target.value,
                              })
                            }
                            className="w-24"
                          />
                          <Button
                            onClick={() => handleQuickAllocate(selectedTask, sprint)}
                            className="flex-1"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Allocate to Sprint
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>

      {/* Create Sprint Modal */}
      {showCreateSprint && (
        <CreateSprintModal
          projectId={projectId!}
          organizationId={project.organizationId}
          onClose={() => setShowCreateSprint(false)}
          onCreate={async (sprint) => {
            try {
              const isDebugMode = typeof window !== 'undefined' &&
                (window.location.hostname === 'localhost' || window.location.search.includes('debugToasts=1'));
              if (isDebugMode) {
                const targetPath = `organizations/${sprint.organizationId}/projects/${sprint.projectId}/sprints`;
                toast.info(`Saving sprint -> ${targetPath}`, { duration: 1800 });
              }
              await addSprint(sprint);
              if (state?.currentUser?.email) {
                await reload(state.currentUser.email);
              }
              setShowCreateSprint(false);
              toast.success('Sprint created');
            } catch (error) {
              console.error('[SprintPlanningBoard] Failed to create sprint:', error);
              toast.error('Failed to create sprint. Please try again.');
            }
          }}
          existingTasks={projectTasks}
        />
      )}
    </div>
  );
}
